import { createColumnHelper } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import Papa from 'papaparse';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LuEllipsis } from 'react-icons/lu';
import { Outlet, useNavigate } from 'react-router-dom';

import { Role } from '@ndvr/auth-api';
import { WithdrawalStatus } from '@ndvr/portfolio-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Page, PageContent, PageTitle, PageTitleRow, PageTopBar } from '@/components/ui/page-layout';
import { accountRoute, formatCurrency, formatDate, titleCase, useHouseholdRoutePaths } from 'utils';
import { downloadFile } from 'utils/file-download';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useAuthContext } from 'views/auth/Auth';
import { CustomTablePage, PageSpinner, Text, TextLink, Tooltip, VerticalLayout } from 'widgets';
import { GlobalSearchTermInput } from 'widgets/form/filter';

import { withdrawalStatusToIcon } from './typeHelper';
import { encodeWithdrawalId, useWithdrawalData } from './withdrawal-hooks';
import { DEFAULT_END_DATE, DEFAULT_START_DATE, WithdrawalMultiFilter } from './WithdrawalMultiFilter';

export const WithdrawalListPage = () => {
    return (
        <Page>
            <PageTopBar />
            <WithdrawalTable />
            <Outlet />
        </Page>
    );
};

export interface WithdrawalTableEntry {
    cashFlowConfigGUID: string;
    withdrawalDate: string;
    withdrawalAmount: string;
    withdrawalStatus: WithdrawalStatus;
    portfolioName?: string;
    accountGroupGUID?: string;
    accountGUID?: string;
    accountName?: string;
    custodyId?: string;
    custodyType?: string;
    householdGUID?: string;
    householdName?: string;
    clientName?: string;
    clientGUID?: string;
    ownerPersonOrgGUID?: string;
}

const columnHelper = createColumnHelper<WithdrawalTableEntry>();

const createColumns = ({
    PortfolioPageRoutePath,
}: Pick<ReturnType<typeof useHouseholdRoutePaths>, 'PortfolioPageRoutePath'>) => [
    columnHelper.accessor('withdrawalDate', {
        header: 'Withdrawal Date',
        cell: ({ getValue }) => formatDate(getValue()),
        sortingFn: 'datetime',
        size: 170,
    }),
    columnHelper.accessor('withdrawalStatus', {
        header: 'Status',
        size: 100,
        cell: ({ getValue }) => <Tooltip tooltip={titleCase(getValue())}>{withdrawalStatusToIcon(getValue())}</Tooltip>,
    }),
    columnHelper.accessor('custodyId', {
        header: 'Brokerage',
        size: 120,
        cell: ({ getValue, row }) => (
            <div>
                {!isEmpty(getValue()) ? (
                    <div>
                        <CopiableGuid
                            guid={getValue() || ''}
                            showDigits="lastFour"
                            showBorder={false}
                            shortenedPrefix="x"
                        />
                    </div>
                ) : (
                    <div>-</div>
                )}
                <div>{titleCase(row.original.custodyType)}</div>
            </div>
        ),
    }),
    columnHelper.accessor('portfolioName', {
        header: 'Portfolio',
        cell: ({ getValue, row }) => (
            <div className="[&_*]:overflow-ellipsis">
                <Tooltip tooltip={getValue()} wrapperClassName="overflow-hidden">
                    <TextLink
                        to={PortfolioPageRoutePath(
                            row.original.householdGUID || '',
                            row.original.accountGroupGUID || '',
                        )}
                    >
                        {getValue()}
                    </TextLink>
                </Tooltip>
            </div>
        ),
    }),
    columnHelper.accessor('accountName', {
        header: 'Account',
        cell: ({ getValue, row }) => (
            <div className="[&_*]:overflow-ellipsis">
                <Tooltip tooltip={getValue()} wrapperClassName="overflow-hidden">
                    <TextLink to={accountRoute(row.original.accountGUID || '')}>{getValue()}</TextLink>
                </Tooltip>
            </div>
        ),
    }),
    columnHelper.accessor('withdrawalAmount', {
        header: 'Amount',
        cell: ({ getValue }) => formatCurrency(getValue(), 'USD'),
        size: 160,
    }),
    columnHelper.accessor('accountGUID', {
        header: 'Account GUID',
        cell: ({ getValue }) => (
            <CopiableGuid guid={getValue() || ''} showDigits="lastFour" showBorder={false} shortenedPrefix="x" />
        ),
        size: 150,
    }),
    columnHelper.accessor((entry) => entry, {
        id: 'hosueholdClient',
        header: 'Client / Household',
        cell: ({ getValue }) => (
            <VerticalLayout>
                <Text>{getValue().clientName}</Text>
                <Text $themeColor="subtle">{getValue().householdName}</Text>
            </VerticalLayout>
        ),
        sortingFn: (rowA, rowB, _columnId) => {
            return rowA.original.householdName?.localeCompare(rowB.original.householdName || '') || 0;
        },
    }),
    // search only hidden columns
    columnHelper.accessor('householdName', {
        enableHiding: true,
    }),
    columnHelper.accessor('clientName', {
        enableHiding: true,
    }),
];

const WithdrawalTable = () => {
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();

    const savedStates = localStorage.getItem('withdrawalStates');
    const withdrawalStates = savedStates ? JSON.parse(savedStates) : [];

    const formMethods = useForm({
        defaultValues: {
            withdrawalStates,
            startDate: DEFAULT_START_DATE,
            endDate: DEFAULT_END_DATE,
        },
    });
    const [startDate, endDate, currentWithdrawalStates] = formMethods.watch([
        'startDate',
        'endDate',
        'withdrawalStates',
    ]);
    // Role checks for custody access
    const iopsPortalRole = useUserHasRole(Role.IOPS_PORTAL_ACCESS);
    const hasCustodyAccess = !!iopsPortalRole.data;
    const isRoleCheckPending = iopsPortalRole.isPending;

    // Use custom hook to fetch and transform withdrawal data
    const {
        withdrawals,
        isLoading: isWithdrawalsLoading,
        isUpdating: isWithdrawalsUpdating,
        loadingStates: { isLoadingTenantAccounts: isTenantAccountsLoading },
    } = useWithdrawalData({
        startDate,
        endDate,
        filterByTenant: !hasCustodyAccess,
    });

    const filtered: WithdrawalTableEntry[] = useMemo(() => {
        return (withdrawals || []).filter((withdrawal: WithdrawalTableEntry) => {
            return !currentWithdrawalStates?.length || currentWithdrawalStates.includes(withdrawal.withdrawalStatus);
        });
    }, [withdrawals, currentWithdrawalStates]);

    const columns = useMemo(() => createColumns({ PortfolioPageRoutePath }), [PortfolioPageRoutePath]);

    if (isWithdrawalsLoading || isRoleCheckPending || (!hasCustodyAccess && isTenantAccountsLoading)) {
        return <PageSpinner />;
    }

    const handleExportCsv = () => {
        const csv = Papa.unparse(filtered);
        downloadFile(
            `withdrawals_table_export_${formatDate(new Date(), 'SERVER')}.csv`,
            window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
        );
    };

    return (
        <FormProvider {...formMethods}>
            <WithdrawalFilterBar onExportCsv={handleExportCsv} count={filtered.length} />
            <PageContent scroll="children" padding="none">
                <WithdrawalTableView
                    data={filtered}
                    isUpdating={isWithdrawalsUpdating}
                    columns={columns}
                    startDate={startDate}
                    endDate={endDate}
                />
            </PageContent>
        </FormProvider>
    );
};

const WithdrawalFilterBar = ({ onExportCsv, count }: { onExportCsv: () => void; count: number }) => {
    const { selectedTenants } = useAuthContext();

    return (
        <PageTitleRow
            actions={
                <div className="flex items-center gap-2">
                    <GlobalSearchTermInput placeholder="Search withdrawals..." round />
                    <WithdrawalMultiFilter />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline2" size="sm" className="px-2">
                                <LuEllipsis className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={onExportCsv}>Export .csv</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            }
            className="gap-4"
        >
            <div>
                <PageTitle>Withdrawals</PageTitle>
                <p className="text-cocoa-600 mt-0.5 text-sm font-thin">
                    {count} withdrawals · {selectedTenants.length} tenants
                </p>
            </div>
        </PageTitleRow>
    );
};

const WithdrawalTableView = ({
    data,
    isUpdating,
    columns,
    startDate,
    endDate,
}: {
    data: WithdrawalTableEntry[];
    isUpdating: boolean;
    columns: ReturnType<typeof createColumns>;
    startDate: Date;
    endDate: Date;
}) => {
    const navigate = useNavigate();

    return (
        <CustomTablePage
            data={data}
            columns={columns}
            exportName="withdrawals"
            rowOnClick={(entry) =>
                navigate(`/trading/withdrawals/${encodeWithdrawalId(entry.cashFlowConfigGUID, entry.withdrawalDate)}`, {
                    state: { listDateRange: { startDate, endDate } },
                })
            }
            initialColumnVisibility={{
                householdName: false,
                clientName: false,
                accountGUID: false,
            }}
            cardClassName="m-0 border-0 shadow-none"
            isUpdating={isUpdating}
        />
    );
};
